if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this.close_button = this.menu ? this.menu.querySelector('.mobile-menu-close') : null;

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.closeMobileMenu();
          }
        }
      });
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          this.closeMobileMenu();
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      if (this.close_button) {
        this.close_button.addEventListener('click', (event) => {
          event.preventDefault();
          const wrapper = this.close_button.closest('details.mobile-toggle-wrapper');
          const toggleButton = (wrapper || this.toggle)?.querySelector('.mobile-toggle');
          if (toggleButton) {
            toggleButton.click();
            return;
          }
          this.closeMobileMenu(wrapper || this.toggle);
        });
      }

      // Consolidated scroll handler — single rAF-driven loop for all reads/writes
      this._scrollTicking = false;
      this._hasAnnouncementBar = !!document.querySelector('.announcement-bar-section');

      const onScroll = () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            // ── Batch reads ──
            const scrollY = window.scrollY;
            const headerRect = this.header_section.getBoundingClientRect();
            const headerHeight = this.clientHeight;
            const isTransparent = this.classList.contains('transparent--true');
            const isStickyActive = this.classList.contains('header-sticky--active');
            const stickyOffset = parseInt(headerRect.top, 10) + document.documentElement.scrollTop;

            let heroThreshold = null;
            if (window.innerWidth < 768 && isTransparent) {
              const hero = document.querySelector('.hero-section, [class*="slideshow"], .banner, .index-section:first-child');
              heroThreshold = hero ? hero.offsetHeight * 0.6 : window.innerHeight * 0.5;
            }

            let announcementHeight = null;
            if (this._hasAnnouncementBar) {
              const a_bar = document.getElementById('shopify-section-announcement-bar');
              if (a_bar) announcementHeight = a_bar.clientHeight;
            }

            // ── Batch writes ──
            if (isStickyActive) {
              const wasSticky = this.classList.contains('is-sticky');
              const shouldStick = scrollY >= stickyOffset && scrollY > 0;
              this.classList.toggle('is-sticky', shouldStick);

              // Luxury fade-out: suppress glass on scroll-up until mouse leaves
              if (wasSticky && !shouldStick && isTransparent) {
                this.classList.add('sh-glass-cooldown');
              }
              // Re-entering sticky clears any pending cooldown
              if (!wasSticky && shouldStick) {
                this.classList.remove('sh-glass-cooldown');
              }
            }

            const isDark = isTransparent && !this.classList.contains('is-sticky');
            this.classList.toggle('sh-header-glass--dark', isDark);

            if (heroThreshold !== null) {
              this.classList.toggle('header--hero-scrolled', scrollY > heroThreshold);
            }

            document.documentElement.style.setProperty('--header-offset', headerRect.top + 'px');
            document.documentElement.style.setProperty('--header-height', headerHeight + 'px');

            if (announcementHeight !== null) {
              document.documentElement.style.setProperty('--announcement-height', announcementHeight + 'px');
            }

            this._scrollTicking = false;
          });
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });

      // Clear glass cooldown when mouse leaves header
      this.addEventListener('mouseleave', () => {
        this.classList.remove('sh-glass-cooldown');
      }, { passive: true });

      // Resize safety: strip mobile-only header classes when viewport goes desktop
      window.addEventListener('resize', this.resetMobileHeroClass.bind(this), {
        passive: true
      });

      // Run immediately to set initial state
      onScroll();
      this.updateGlassMode();

      if (this._hasAnnouncementBar) {
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }
    // Unified glass mode: transparent header on dark hero → dark glass surface
    updateGlassMode() {
      const isDark = this.classList.contains('transparent--true') && !this.classList.contains('is-sticky');
      this.classList.toggle('sh-header-glass--dark', isDark);
    }
    // Mobile-only: on homepage, add header--hero-scrolled when user scrolls past hero
    setMobileHeroClass() {
      if (window.innerWidth >= 768) return;
      if (!this.classList.contains('transparent--true')) return;
      const hero = document.querySelector('.hero-section, [class*="slideshow"], .banner, .index-section:first-child');
      const threshold = hero ? hero.offsetHeight * 0.6 : window.innerHeight * 0.5;
      this.classList.toggle('header--hero-scrolled', window.scrollY > threshold);
    }
    // Resize safety: remove mobile header classes when viewport becomes desktop
    resetMobileHeroClass() {
      if (window.innerWidth >= 768) {
        this.classList.remove('header--hero-scrolled');
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }

    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }

    closeMobileMenu(wrapper) {
      const target = wrapper || this.toggle;
      if (!target) {
        return;
      }
      const menu = target.querySelector('#mobile-menu');
      document.body.classList.remove('overflow-hidden');
      if (menu) {
        menu.classList.remove('active');
      }
      target.classList.remove('active');
      target.open = false;
      target.removeAttribute('open');
      this.closeAnimation(target);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      window.addEventListener('resize', debounce(function () {
        _this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(function () {
        _this.resizeSubMenus();
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}